import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { useImportStationTextStore } from '../../shared/store/importStationTextStore';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { useTextBlock } from '../../shared/hooks/useApi/useTextBlock';
import SimpleTextBlock from './SimpleTextBlock';
import { TextBlockData } from '../../shared/dto/EntityRepository';
import { useProjectElementActions } from '../../shared/hooks/useApi/useProjectElement';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { getBtnName } from '../../shared/constants/btnNames';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { paperTextureModal } from '../../shared/component/paperTexture';

const ImportStationForTextBlock: React.FC = () => {
  const { isClosing, standardModal, clearModal } = useModalStore();
  const { simpleSubList, selectedSubIdList, clearSelectedSubIdList, clearSimpleList } = useImportStationTextStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { getTextBlockListForImport } = useTextBlock();
  const { addTextBlockInProjectElement } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();

  useEffect(() => {
    if (!standardModal) return;
    const textId = standardModal.value!;
    getTextBlockListForImport(textId);

    return () => {
      clearSimpleList();
    };
  }, []);

  const handleSubmit = async () => {
    if (!project || !standardModal?.title || !standardModal?.value) return;
    if (selectedSubIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await addTextBlockInProjectElement({
      projectElementId: standardModal.title,
      textId: standardModal.value,
      textBlockIds: selectedSubIdList,
    });
  };

  const handleCancel = () => {
    clearSelectedSubIdList();
    clearSimpleList();
    clearModal();
  };

  if (!canShowProject || !project) return null;

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ImportComp>
        <ImportMainTitleWrapper>
          <ImportTitle>{getModalMessage('SELECT_TEXT_BLOCK_TO_ADD')}</ImportTitle>
        </ImportMainTitleWrapper>
        <ImportContentWrapper>
          <ImportContentContainer>
            {simpleSubList.map((tb) => (
              <SimpleTextBlock
                key={tb.id}
                data={tb as TextBlockData}
                projectElementId={standardModal?.title ?? undefined}
              />
            ))}
          </ImportContentContainer>
        </ImportContentWrapper> 
        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('IMPORT')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
          <HeadlessBtn
            value={getBtnName('CANCEL')}
            handleClick={handleCancel}
            StyledBtn={PaperBlurFitBtn}
          />
        </ButtonContainer>
      </ImportComp>
    </LeftModalFrame>
  );
};

export const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: 4vw;
  width: calc(30vw + 40px);
  min-width: 30vw;
  height: 85vh;
  padding: 20px;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(-100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const ImportComp = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 3vh;
`;

const ImportMainTitleWrapper = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportContentWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  height: 60vh;

  &::before{
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 8px;
    pointer-events: none;
    z-index: 1;
  }
  &::before {
    top: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.05),
      transparent
    );
  }
  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 80px;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    bottom: 0;
    background: linear-gradient(
      to top,
      rgba(228, 228, 228, 0.2),
      transparent
    );
  }
`;

const ImportContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  bottom: 20px;
  right: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default ImportStationForTextBlock;
